import { Undo2Icon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ReversalDialog } from "@/components/commons/reversal-dialog"
import { Button } from "@/components/ui/button"
import { usePermission } from "@/contexts/permission"
import { PermissionsRolesEnum } from "@/enums"
import { useReverseOrderMutation } from "@/services/orders/reverse-order"
import { useReverseCycleMutation } from "@/services/subscriptions/reverse-cycle"

interface CycleOrigin {
  subscriptionId: string
  cycleId: string
}

interface ReversalActionProps {
  orderId: string
  /**
   * Preenchido quando o pedido nasceu de um ciclo de assinatura. É o que decide a API: existe uma
   * para cada cenário, e a diferença não é de conveniência — o core resolve o ciclo pelo par
   * assinatura + ciclo, e reverter pelo uuid do pedido acertaria outro alvo.
   */
  cycleOrigin?: CycleOrigin | null
}

/**
 * A ação de reverter na página do pedido.
 *
 * O pedido pode ter nascido avulso ou de um ciclo de assinatura, e cada origem tem sua API. Quem
 * decide é o dado — não o canal de venda, que já escondeu a ação de pedidos que precisavam dela.
 */
export const ReversalAction = ({
  orderId,
  cycleOrigin,
}: ReversalActionProps) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermission()
  const [open, setOpen] = useState(false)

  const canReverse = hasPermission([
    PermissionsRolesEnum.ADMIN,
    PermissionsRolesEnum.ORDERS_WRITE,
  ])

  const { mutateAsync: reverseOrder } =
    useReverseOrderMutation()
  const { mutateAsync: reverseCycle } =
    useReverseCycleMutation()

  const scope = cycleOrigin ? "cycleReversal" : "reversal"
  const strings = cycleOrigin
    ? {
        title: t("subscriptions.cycleReversal.title"),
        description: t(
          "subscriptions.cycleReversal.description"
        ),
        button: t("subscriptions.cycleReversal.button"),
        success: t("subscriptions.cycleReversal.success"),
        successForced: t(
          "subscriptions.cycleReversal.successForced"
        ),
      }
    : {
        title: t("orders.reversal.title"),
        description: t("orders.reversal.description"),
        button: t("orders.reversal.button"),
        success: t("orders.reversal.success"),
        successForced: t("orders.reversal.successForced"),
      }

  const handleConfirm = ({
    reason,
    forced,
  }: {
    reason: string
    forced: boolean
  }) =>
    cycleOrigin
      ? reverseCycle({ ...cycleOrigin, reason, forced })
      : reverseOrder({ orderId, reason, forced })

  return (
    <ReversalDialog
      description={strings.description}
      onConfirm={handleConfirm}
      open={open}
      setOpen={setOpen}
      successForcedMessage={strings.successForced}
      successMessage={strings.success}
      title={strings.title}
      trigger={
        <Button
          className="gap-2 border-red-500 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 disabled:opacity-50"
          data-testid={`order-reversal-${scope}`}
          disabled={!canReverse}
          size="sm"
          variant="outline"
        >
          <Undo2Icon size={18} />
          {strings.button}
        </Button>
      }
    />
  )
}
